/**
 * DriveZone — QuotaModal
 * Shown as a centered overlay when a free user hits their search limit (HTTP 402).
 * Does NOT navigate away — user stays on the search page.
 */

import { useNavigate } from 'react-router-dom'
import { X, Zap, Gift, Copy, CheckCheck, MessageCircle } from 'lucide-react'
import { useSearchStore } from '@/stores/searchStore'
import { useAuthStore } from '@/stores/authStore'
import { useState } from 'react'

const PAYBILL   = '0748272022'
const WHATSAPP  = '+254748272022'

export default function QuotaModal() {
  const navigate          = useNavigate()
  const setShowQuotaModal = useSearchStore((s) => s.setShowQuotaModal)
  const { user }          = useAuthStore()

  const [copied, setCopied] = useState(false)

  function handleClose()  { setShowQuotaModal(false) }
  function handleRefer()  { setShowQuotaModal(false); navigate('/account#refer') }

  function copyPaybill() {
    navigator.clipboard.writeText(PAYBILL).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const whatsappMsg = encodeURIComponent(
    `Hi, I'd like to subscribe to DriveZone Pro. My email: ${user?.email ?? ''}. Please add me to Pro so I can confirm before paying KES 300.`
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-sm card p-6 space-y-4 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center">
            <Zap className="w-6 h-6 text-primary-600" />
          </div>
        </div>

        {/* Heading */}
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold text-slate-900">You've used all your free searches</h2>
          <p className="text-sm text-slate-500">
            Get Pro for <span className="font-semibold text-slate-700">KES 300 / month</span> — unlimited searches.
          </p>
        </div>

        {/* Steps */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">How to subscribe</p>

          {/* Step 1 */}
          <div className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] font-bold
                             flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
            <div className="flex-1">
              <p className="text-xs text-slate-600 mb-1.5">Message us on WhatsApp — we'll add you to Pro first:</p>
              <a
                href={`https://wa.me/${WHATSAPP.replace('+', '')}?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClose}
                className="flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600
                           text-white text-xs font-semibold rounded-lg px-3 py-2 transition-colors w-full"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                WhatsApp {WHATSAPP}
              </a>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] font-bold
                             flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
            <p className="text-xs text-slate-600 pt-0.5">
              Check your{' '}
              <button
                onClick={() => { handleClose(); navigate('/account') }}
                className="text-primary-600 underline"
              >
                account page
              </button>
              {' '}— once you see <strong>Pro</strong>, you've been added.
            </p>
          </div>

          {/* Step 3 */}
          <div className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] font-bold
                             flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-600">Then send <strong>KES 300</strong> via M-Pesa to confirm:</p>
              <div className="flex items-center gap-2 mt-1 bg-white border border-slate-200
                              rounded-lg px-2.5 py-1.5">
                <span className="text-sm font-mono font-semibold text-slate-900 flex-1">{PAYBILL}</span>
                <button onClick={copyPaybill} className="text-primary-600 flex-shrink-0" title="Copy">
                  {copied
                    ? <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />
                    : <Copy className="w-3.5 h-3.5" />
                  }
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Refer CTA */}
        <button
          onClick={handleRefer}
          className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
        >
          <Gift className="w-4 h-4" /> Refer a friend — get one week of free searches
        </button>

        <button
          onClick={handleClose}
          className="w-full text-sm text-slate-400 hover:text-slate-600 py-1 transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  )
}
